import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Input, Textarea } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { draftEmail, saveGmailDraft } from '@/data/client';
import type { DraftKind, EmailDraft } from '@/data/types';
import { useGoogleStatus, ConnectGoogleButton } from './GoogleConnect';
import { FormError, Modal } from './Modal';

export const DRAFT_LABEL: Record<DraftKind, string> = { reply: 'Reply', follow_up: 'Follow-up', outreach: 'Outreach', nurture: 'Nurture email' };

/* The deal page's email assistant: pick what to write; the draft opens to edit. */
export function EmailAssistantCard({ nurture, hasInbound, onDraft }: { nurture: boolean; hasInbound: boolean; onDraft: (kind: DraftKind) => void }) {
  return (
    <Card padding={false} className="crm-section">
      <div className="crm-section__head"><h3>Email assistant</h3></div>
      <p className="crm-cell-sub" style={{ marginTop: -8, marginBottom: 10 }}>Drafted from this deal's conversations, Vault and the coach's read. You review it; it saves to your Gmail drafts — nothing is sent.</p>
      <div className="crm-import-choice" style={{ flexWrap: 'wrap' }}>
        {hasInbound && <Button size="sm" variant="outline" onClick={() => onDraft('reply')}>Reply</Button>}
        <Button size="sm" variant="outline" onClick={() => onDraft('follow_up')}>Follow-up</Button>
        <Button size="sm" variant="outline" onClick={() => onDraft('outreach')}>Outreach</Button>
        {nurture && <Button size="sm" variant="outline" onClick={() => onDraft('nurture')}>Nurture email</Button>}
      </div>
    </Card>
  );
}

/* Draft → edit → (rewrite with instructions) → save to Gmail drafts or copy. */
export function DraftModal({ dealId, dealName, kind, instructions: initial, onClose }: {
  dealId: string; dealName: string; kind: DraftKind; instructions?: string; onClose: () => void;
}) {
  const qc = useQueryClient();
  const { data: google } = useGoogleStatus();
  const [draft, setDraft] = useState<EmailDraft | null>(null);
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [instructions, setInstructions] = useState(initial ?? '');
  const [copied, setCopied] = useState(false);
  const write = useMutation({
    mutationFn: (revise: boolean) => draftEmail(dealId, {
      kind, instructions: instructions.trim() || undefined, ...(revise && draft ? { previous: { subject, body } } : {}),
    }),
    onSuccess: (d) => { setDraft(d); setTo(d.to?.email ?? to); setSubject(d.subject); setBody(d.body); },
  });
  const save = useMutation({
    mutationFn: () => saveGmailDraft(dealId, { kind, to: to.trim(), subject: subject.trim(), body, inReplyTo: draft?.inReplyTo ?? null }),
    onSuccess: () => { for (const k of ['deal', 'deals', 'followUps']) void qc.invalidateQueries({ queryKey: [k] }); },
  });
  // Draft on open.
  useEffect(() => { write.mutate(false); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const canSave = google?.draftsGranted;
  return (
    <Modal title={`${DRAFT_LABEL[kind]} — ${dealName}`} wide onClose={onClose} footer={save.data ? <>
      <a className="tr-btn tr-btn--outline" href={save.data.url} target="_blank" rel="noreferrer">Open in Gmail</a>
      <Button onClick={onClose}>Done</Button>
    </> : <>
      <Button variant="ghost" onClick={async () => { await navigator.clipboard.writeText(`Subject: ${subject}\n\n${body}`); setCopied(true); }} disabled={!body}>
        {copied ? 'Copied' : 'Copy'}
      </Button>
      <Button onClick={() => save.mutate()} disabled={!canSave || !body || !to.trim() || save.isPending} iconLeft={<Icon name="mail" size={14} />}>
        {save.isPending ? 'Saving…' : 'Save to Gmail drafts'}
      </Button>
    </>}>
      <div className="crm-form">
        {write.isPending && !draft && <p className="crm-cell-muted">Writing from the latest conversations…</p>}
        {draft && <>
          <Input label="To" value={to} onChange={(e) => setTo(e.target.value)} placeholder="name@company.com" />
          <Input label="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
          <Textarea label="Message" rows={12} value={body} onChange={(e) => setBody(e.target.value)} />
        </>}
        <div className="crm-addopt">
          <Input label={draft ? 'Change it' : 'Instructions (optional)'} placeholder="e.g. shorter, mention the June start date, offer two call times"
            value={instructions} onChange={(e) => setInstructions(e.target.value)} />
          <Button variant="outline" size="sm" onClick={() => write.mutate(!!draft)} disabled={write.isPending} iconLeft={<Icon name="sparkles" size={13} />}>
            {write.isPending ? 'Writing…' : draft ? 'Rewrite' : 'Write'}
          </Button>
        </div>
        {google && !canSave && (
          <div className="crm-callout">
            Saving to Gmail drafts needs one more Google permission. You can copy the draft meanwhile.
            <div style={{ marginTop: 8 }}><ConnectGoogleButton label="Allow Gmail drafts" /></div>
          </div>
        )}
        {save.data && <p className="crm-callout"><Icon name="check-circle" size={14} /> Saved to your Gmail drafts. Review and send it from Gmail — the sent email lands back on the timeline.</p>}
        <FormError error={write.error ?? save.error} />
      </div>
    </Modal>
  );
}
